import { ShareButtonSecondary } from 'components/common-form/sidePanel/docToolbar/summary/ShareButtonSecondary';
import { moreAgressiveColorMapper } from 'components/PostTrade/ProductModification/components/ValidationErrors.utils';
import type { TradedSidePanelProps } from 'components/secondaryMarket/live/sidePanel/traded/TradedSidePanel.connect';
import { ConnectedOrderPlacedActionButtons } from 'components/secondaryMarket/shared/SidePanel/OrderPlacedActionButtons';
import { ConnectedTradeWay } from 'components/secondaryMarket/shared/SidePanel/TradeWay.connect';
import { MessagesPanel, Price, useFormattedPrice } from 'components/secondaryMarket/shared/status';
import { useErrorMessagesFormatter } from 'components/secondaryMarket/shared/status/messages/useErrorMessageFormatter';
import { PanelFooter, PanelHeader } from 'components/secondaryMarket/shared/status/Panel';
import { useTradeWayLabel } from 'components/secondaryMarket/shared/status/tradeWay/useTradeWayLabel';
import { useEffectOnce } from 'hooks/useEffectOnce';
import { useUserNotification } from 'hooks/useUserNotification';
import { FC } from 'react';
import { useIntl } from 'react-intl';
import type { HighlightedContent, Msg } from 'state/notifications/notification.model';

import { FlexColumnPanel } from '../../../shared/SidePanel/FlexColumnPanel';
import { QuotedDetails } from '../quoted/QuotedDetails';

export const TradedSidePanel: FC<TradedSidePanelProps> = ({
  formId,
  onDone,
  price,
  quoteId,
  priceCurrency,
  priceType,
  closeTabWhenLiveTradeCpOrderIsPlacedActivated,
  validationErrors,
  initialUpfront,
  tradeWay,
  salesMargin,
  salesMarginAdjustment,
  tradingPrice,
  tradingPriceAdjustment,
  upfrontAdjusted,
  settlementDate,
  effectiveCashAmount,
  quantity,
  tradeDate,
  accruedInterest,
  quotationType,
  hasOrderBlotter,
}) => {
  const { formatMessage } = useIntl();
  const { formattedNumber, formattedType } = useFormattedPrice({ price, priceCurrency, priceType });
  const { info, warn } = useUserNotification();
  const settlementPriceLabel = formatMessage({ id: 'secondaryMarket.sidePanel.label.settlementPrice' });
  const tradeWayLabel = useTradeWayLabel({ way: tradeWay });
  const quoteIdLabel = formatMessage({ id: 'secondaryMarket.sidePanel.label.quoteId' });
  const messages = useErrorMessagesFormatter(validationErrors, moreAgressiveColorMapper);
  useEffectOnce(() => {
    if (!closeTabWhenLiveTradeCpOrderIsPlacedActivated) {
      return;
    }
    const title = formatMessage({ id: 'secondaryMarket.sidePanel.label.tradeDone' });
    const content: Msg[] = [
      { content: `${settlementPriceLabel} : ${formattedNumber} ${formattedType}`, severity: 'info' },
      { content: tradeWayLabel!, severity: 'info' },
      { content: `${quoteIdLabel} : ${quoteId}`, severity: 'info' },
    ];
    if (messages?.length > 0) {
      warn({
        title: { content: title, severity: 'warning' },
        content: [
          ...content,
          ...messages.map(m => ({ isHtml: true, content: m.htmlMessage, severity: 'warning' }) as HighlightedContent),
        ],
      });
    } else {
      info({
        title: { content: title, severity: 'info' },
        content,
      });
    }

    onDone(true);
  });

  return (
    <FlexColumnPanel>
      <PanelHeader color="info" discreet>
        <div className="d-flex justify-content-between">
          <div className="d-flex flex-column">
            <ConnectedTradeWay formId={formId} />
            {settlementPriceLabel}
          </div>
          <ShareButtonSecondary
            tabId={formId}
            color="tradable"
            mustForceAdvancedMode={false}
            workspaceType="secondaryMarketQuotes"
            type="live"
          />
        </div>
        <Price dataE2e="settlement-price" price={price} priceType={priceType} priceCurrency={priceCurrency}></Price>
      </PanelHeader>
      <PanelFooter color="info" statusKey="secondaryMarket.sidePanel.label.tradeDone" icon="check_circle" />
      <MessagesPanel messagesToDisplay={messages} className="pt-2" e2e="side-panel-header" />
      <div className="flex-grow-1 m-4">
        <QuotedDetails
          quoteId={quoteId}
          priceType={priceType}
          tradeWay={tradeWay}
          salesMargin={salesMargin}
          salesMarginAdjustment={salesMarginAdjustment}
          tradingPrice={tradingPrice}
          tradingPriceAdjustment={tradingPriceAdjustment}
          upfrontAdjusted={upfrontAdjusted}
          upfront={initialUpfront}
          effectiveCashAmount={effectiveCashAmount}
          settlementPrice={price}
          settlementDate={settlementDate}
          tradeDate={tradeDate}
          currency={priceCurrency}
          quantity={quantity}
          accruedInterest={accruedInterest}
          quotationType={quotationType}
        />
      </div>
      <ConnectedOrderPlacedActionButtons
        onDone={onDone}
        hasOrderBlotter={hasOrderBlotter}
        orderId={quoteId}
        blotterRoute="/secondary-orders-blotter"
      />
    </FlexColumnPanel>
  );
};
TradedSidePanel.displayName = 'TradedSidePanel';
